import React, {useMemo, useState} from 'react'
import _ from 'lodash'
import dayjs from 'dayjs'
import {message} from 'antd'
import {IoTrashOutline} from 'react-icons/io5'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {API_ROOT_URL} from 'envs/_current/config'
import {LookupTagsPicker} from '../../custom/TagsPicker'
import {
    familyBook_addFamilyBookNote_Api,
    familyBook_deleteFamilyBookNote_Api,
    familyBook_getFamilyBookNotes_Api,
    familyBook_updateFamilyBookInfoAsParish_Api,
} from 'apis/familyBook'

// Tab/khối "Ghi chú mục vụ" - DÙNG CHUNG cho CẢ 2 nơi giáo xứ xem 1 sổ: FamilyBookDetail.js (trang
// cá nhân, khi giáo xứ đang xem sổ CHÍNH HỌ nhập - is_org_managed) VÀ ParishBookReviewDrawer.js
// (drawer duyệt sổ giáo dân tự tạo, LUÔN ở góc độ giáo xứ). Tách thành 1 component riêng thay vì
// chép lại 2 lần - CHỈ nhận field thô (ownerType/ownerId/familyBookId/pastoralTag), KHÔNG giả định
// hình dạng `item` cụ thể của từng nơi gọi (2 API nguồn gốc khác nhau - FamilyBookTransformer đầy đủ
// vs response thủ công của getFamilyBookForParishReview()).
//
// CHỈ giáo xứ xem được (nơi gọi tự chịu trách nhiệm GATE trước khi render component này - xem
// is_org_managed && edit ở FamilyBookDetail, hoặc mặc định vì cả drawer vốn CHỈ giáo xứ vào được).
// 2 khối: pastoral_tag (hashtag TÁI DÙNG qua bảng `lookups` có sẵn, KHÔNG bảng riêng) + nhật ký ghi
// chú CÓ NGÀY (family_book_notes).
const FamilyBookPastoralNotesTab = ({ownerType, ownerId, familyBookId, pastoralTag, t}) => {
    const [tags, setTags] = useState(pastoralTag ? pastoralTag.split(',').filter(Boolean) : [])
    const [noteText, setNoteText] = useState('')
    const ownerQuery = {type: ownerType, id: ownerId, family_book_id: familyBookId}

    const {handleAsyncAction: doSaveTags, loading: savingTags} = useAsyncAction({
        apiInfo: familyBook_updateFamilyBookInfoAsParish_Api,
        onSuccess: () => message.success(t('saved')),
        onError: () => message.error(t('error occurred')),
    })

    const {response: notesResponse, handleAsyncAction: refetchNotes} = useAsync({
        apiInfo: familyBook_getFamilyBookNotes_Api,
        query: bindQueryParams([{type: ownerType}, {id: ownerId}, {family_book_id: familyBookId}]),
        deps: [familyBookId],
    })
    const notes = _.get(notesResponse, 'data.data', [])

    const {handleAsyncAction: doAddNote, loading: addingNote} = useAsyncAction({
        apiInfo: familyBook_addFamilyBookNote_Api,
        onSuccess: () => {
            setNoteText('')
            refetchNotes()
        },
        onError: () => message.error(t('error occurred')),
    })

    const {handleAsyncAction: doDeleteNote} = useAsyncAction({
        apiInfo: familyBook_deleteFamilyBookNote_Api,
        onSuccess: () => refetchNotes(),
        onError: () => message.error(t('error occurred')),
    })

    // path CHỌN THẲNG owner_type/owner_id CỦA GIÁO XỨ - dùng route getByOwnerAndType có sẵn
    // (LookupController), tự khoanh vùng đúng giáo xứ, KHÔNG cần API/bảng riêng cho tag.
    const tagsApiInfo = useMemo(() => ({
        root: API_ROOT_URL,
        path: `/lookup/${ownerType}/${ownerId}/field/family_book_pastoral_tag/gets`,
        method: 'GET',
        paramFields: ['keyword'],
        fields: ['keyword'],
    }), [ownerType, ownerId])

    return (
        <div className="flex flex-col gap-3">
            <div className="p-3 flex flex-col gap-2 background border border-color-50 shadow-sm rounded-2xl">
                <span className="font-bold text-xs text-color-400 uppercase tracking-widest">{t('pastoral tags')}</span>
                <span className="text-2xs text-color-400 leading-snug">{t('pastoral tags hint')}</span>
                <LookupTagsPicker
                    value={tags}
                    apiInfo={tagsApiInfo}
                    onChange={(e) => setTags(e?.target?.value || [])}
                />
                <button
                    type="button"
                    disabled={savingTags}
                    onClick={() => doSaveTags({pastoral_tag: tags.join(',')}, ownerQuery)}
                    className="self-end px-3 py-1.5 rounded-lg bg-family-600 hover:bg-family-700 text-white text-xs font-semibold cursor-pointer disabled:opacity-40">
                    {t('save')}
                </button>
            </div>

            <div className="p-3 flex flex-col gap-3 background border border-color-50 shadow-sm rounded-2xl">
                <span className="font-bold text-xs text-color-400 uppercase tracking-widest">{t('pastoral notes')}</span>
                <div className="flex flex-col gap-2">
                    <textarea
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder={t('input your note')}
                        rows={2}
                        className="w-full p-2 border border-color-100 rounded-lg text-sm resize-none"
                    />
                    <button
                        type="button"
                        disabled={addingNote || !noteText.trim()}
                        onClick={() => doAddNote({note: noteText}, ownerQuery)}
                        className="self-end px-3 py-1.5 rounded-lg bg-family-600 hover:bg-family-700 text-white text-xs font-semibold cursor-pointer disabled:opacity-40">
                        {t('add note')}
                    </button>
                </div>
                <div className="flex flex-col gap-2">
                    {notes.length === 0 && (
                        <span className="text-xs text-color-300 italic">{t('no notes yet')}</span>
                    )}
                    {notes.map((n) => (
                        <div key={n.id} className="p-2.5 flex flex-col gap-1 bg-color-50 rounded-lg">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-2xs text-color-400">
                                    {n.creator?.name}{n.creator?.name && n.created_at ? ' · ' : ''}
                                    {n.created_at ? dayjs(n.created_at).format('DD/MM/YYYY HH:mm') : ''}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => doDeleteNote({}, {...ownerQuery, note_id: n.id})}
                                    className="p-1 rounded cursor-pointer text-color-300 hover:text-rose-600 hover:bg-rose-50">
                                    <IoTrashOutline size={13}/>
                                </button>
                            </div>
                            <span className="text-sm text-color-000 whitespace-pre-wrap">{n.note}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default FamilyBookPastoralNotesTab
